import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { Input } from 'antd';
import { TruncatedText } from '@dynamika-shared/ui/TruncatedText/TruncatedText';
import { StatusPill } from '@dynamika-shared/ui/StatusPill/StatusPill';

import { SearchSuffix } from '@dynamika-shared/ui/Input/SearchSuffix';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { Tabs } from '@dynamika-shared/ui/Tabs/Tabs';
import { Table } from '@dynamika-shared/ui/Table/Table';
import { SubjectListResponse } from '@restore/SiteSubjectsList/SiteSubjectsList.types';
import {
  SiteSubjectTypeForm,
  SubjectCreate,
} from '@components/modals/SubjectCreate/SubjectCreate';
import { SiteSubjectType } from '@dynamika-shared/types/subjects';
import { makeSubjectListColumns } from './SubjectListTable.columns';

import { useMakePagination } from '@restore/paginationAndSort/paginationAndSort.slice';
import { CohortType } from '@dynamika-shared/types/cohorts';
import { StudyStatusEnum } from '@dynamika-shared/types/status';
import { SiteUserUploadDrawer } from 'src/components/SiteUserUpload/SiteUserUploadDrawer';
import { SubjectListHeader } from './SubjectListHeader';

type TabItem = {
  key: string;
  label: ReactNode;
  children: null;
};

type ClinicalTrialListPageViewProp = {
  data?: Partial<SubjectListResponse>;
  clinicalTrialId: string;
  siteId: string;
  loading?: boolean;
  siteCode?: string;
  clinicalTrialName?: string;
  onSort: (columnName: string, sortState?: string) => void;
  tabsItems?: TabItem[];
  handleTabChange: (key: string) => void;
  onCreateNewSubject: (data: SiteSubjectTypeForm) => Promise<void>;
  cohorts: CohortType[];
  configStatus?: StudyStatusEnum;
  onHighlightFinish: () => void;
  newlyCreatedSubjectCode: string | null;
  sortState: string | undefined;
  onSearch: (value: string) => void;
};

export const SubjectListView: FC<ClinicalTrialListPageViewProp> = ({
  data,
  loading,
  onSort,
  onHighlightFinish,
  clinicalTrialName,
  clinicalTrialId,
  siteId,
  siteCode,
  tabsItems,
  handleTabChange,
  onCreateNewSubject,
  cohorts,
  configStatus,
  newlyCreatedSubjectCode,
  sortState,
  onSearch,
}) => {
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [subject, setSubject] = useState<SiteSubjectType | null>();
  const [searchValue, setSearchValue] = useState('');
  const clinicalTrialsColumns = makeSubjectListColumns(setSubject, cohorts);

  const totalElements = data?.totalElements ?? 0;
  const pagination = useMakePagination('subjectList', !!data?.totalElements);

  const createNewSubject = async (data: SiteSubjectTypeForm) => {
    await onCreateNewSubject(data);
    setShowCreateModal(false);
  };

  const handleClearInput = () => {
    setSearchValue('');
    onSearch('');
  };

  const shouldHighlightRow = useCallback(
    (row: number | SiteSubjectType) => {
      if (typeof row === 'number') return;
      return row.code === newlyCreatedSubjectCode;
    },
    [newlyCreatedSubjectCode]
  );

  useEffect(() => {
    onSearch('');
    setSearchValue('');
  }, [clinicalTrialId, siteId]);

  return (
    <>
      <div className='flex items-center justify-between pb-2 pt-3'>
        <div className='text-12 flex items-center'>
          {clinicalTrialName && (
            <TruncatedText
              text={clinicalTrialName}
              truncate
              tooltip
              className={
                'mr-1 text-6xl font-medium max-w-[50rem] leading-[55px]'
              }
            />
          )}

          {configStatus && <StatusPill status={configStatus} />}
        </div>
      </div>

      <div>
        <Tabs
          defaultActiveKey={siteId}
          activeKey={siteId}
          items={tabsItems}
          onChange={handleTabChange}
          blocked={false}
        />
      </div>

      <div className='flex justify-between pb-3 pt-[13px]'>
        <SubjectListHeader totalElements={totalElements} />

        <div className='flex items-center'>
          <Input
            placeholder='Search Subject ID...'
            className='min-w-[400px] max-w-lg rounded-[32px] px-2 py-[10px] text-1424 text-spanishGray'
            style={{ boxShadow: 'none' }}
            suffix={
              <SearchSuffix action={handleClearInput} disabled={!searchValue} />
            }
            value={searchValue}
            onChange={({ target: { value } }) => {
              const upperCaseValue = value.toUpperCase();
              setSearchValue(upperCaseValue);
              onSearch(upperCaseValue);
            }}
          />

          <Button
            className='ml-1 min-w-[144px] !bg-blackOlive text-white hover:!border-chineseBlack hover:!bg-dodgerBlue'
            iconType='plus'
            onClick={() => setShowCreateModal(true)}
          >
            Subject
          </Button>
        </div>
      </div>

      <Table
        columns={clinicalTrialsColumns}
        data={data?.content || []}
        totalItems={data?.totalElements ?? 0}
        loading={loading}
        onSort={onSort}
        loadingLength={8}
        shouldHighlightRow={shouldHighlightRow}
        onHighlightFinish={onHighlightFinish}
        sortState={sortState}
        pagination={pagination}
      />
      {showCreateModal && (
        <SubjectCreate
          toggleVisibility={setShowCreateModal}
          onSubmit={createNewSubject}
          cohorts={cohorts}
        />
      )}
      {subject && (
        <SiteUserUploadDrawer
          subject={subject}
          siteCode={siteCode}
          setSubject={setSubject}
        />
      )}
    </>
  );
};
